import { IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Check, Clear } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { APICallStatus, Booking, BookingStatus, DataTypes, UserType } from "../../Types/UnitFeatureTypes";
import { useAuth } from "../../context/authContext";
import { Actions } from "../../Redux/Reducer/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { approveBookingAPIStatus, rejectBookingAPIStatus } from "../../Redux/selectors";
import { useApp } from "../../context/appContext";
import { set } from "date-fns";

interface BuildBookingsTableProps {
    item: Booking[];
    showActions?: boolean;
}

const BuildBookingsTable: React.FC<BuildBookingsTableProps> = ({
    item,
    showActions = true
}) => {

    const navigate = useNavigate();
    const { user } = useAuth();
    const dispatch = useDispatch();
    const approveApiStatus = useSelector(approveBookingAPIStatus);
    const rejectApiStatus = useSelector(rejectBookingAPIStatus);
    const IS_MANAGER = user?.type === UserType.MANAGER;
    const { setSnackbar } = useApp();

    useEffect(() => {
        if (approveApiStatus === APICallStatus.SUCCESS || rejectApiStatus === APICallStatus.SUCCESS) {
            dispatch(Actions.fetchBooking.bookingActions.request({}));
            dispatch(Actions.booking.resetStatusRequestAction());
            setSnackbar(true, 'Booking status updated successfully', 'success');
        } else if (approveApiStatus === APICallStatus.FAILURE || rejectApiStatus === APICallStatus.FAILURE) {
            dispatch(Actions.booking.resetStatusRequestAction());
            setSnackbar(true, 'Failed to update booking status', 'error');
        }
        //  else if () {
        //     alert('Failed to reject booking');
        //     dispatch(Actions.booking.resetStatusRequestAction());
        // }
    }, [approveApiStatus, rejectApiStatus]);

    const getTableData = (item: Booking[]) => {
        return (<Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{fontWeight: 600}} align="center" >Created Date</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Amenity Name</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center" >Booking Date</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">User</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Unit</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Status</TableCell>
                    {showActions && <TableCell sx={{fontWeight: 600}} align="center">Quick Actions</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {item.map((booking: Booking) =>
                    <TableRow key={booking.id} sx={{'&:hover': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer'
                    }}} onClick={(e) => {
                        if ((e.target as HTMLElement).closest('#actions')) return;
                        booking.id !== undefined && handleBookingExpanded(booking.id)
                    }}>
                        <TableCell align="center">{new Date(booking.created_on).toDateString()}</TableCell>
                        <TableCell sx={{maxWidth: 100}} align="center">
                            <Typography variant="body2" color="textSecondary" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {booking.amenity.title}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{maxWidth: 400}} align="center">
                            <Tooltip title={new Date(booking.booking_start).toDateString()}>
                                <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {new Date(booking.booking_start).toDateString()}
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" color="textSecondary" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {booking.requesting_user?.name}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">{booking.requester_unit?.unit_number}</TableCell>
                        {/* <TableCell align="center">
                            <Typography variant="body2" color="textSecondary">{new Date(booking.bookingDate.end).toDateString()}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" color="textSecondary">{booking.requestingUser.name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" color="textSecondary">{booking.requesterUnit}</Typography>
                        </TableCell> */}
                        <TableCell align="center">
                            <Typography variant="body2" color="textSecondary">{booking.status}</Typography>
                        </TableCell>
                        {showActions && (booking.status !== BookingStatus.APPROVED && booking.status !== BookingStatus.REJECTED && booking.status !== BookingStatus.CANCELLED && IS_MANAGER && booking.amenity.require_approval ? <TableCell align="center" id="actions">
                            <Tooltip title="Approve Request">
                                <IconButton onClick={() => booking.id !== undefined && handleApproveBooking(booking.id)}><Check /></IconButton> 
                            </Tooltip>
                            <Tooltip title="Reject Request">
                                <IconButton onClick={() => booking.id !== undefined && handleRejectBooking(booking.id)}><Clear /></IconButton> 
                            </Tooltip>
                        </TableCell>: <TableCell align="center"/>)}
                    </TableRow>
                )}
            </TableBody>
        </Table>)
    }
    
    if (!item || !item.length) {
        return (
            <Paper elevation={3} sx={{borderRadius: 3, padding: 2, alignItems: 'center', display: 'flex', justifyContent: 'center'}}>
                <Typography variant="subtitle1" fontWeight={600} color="textSecondary">No Booking Available</Typography>
            </Paper>
        )
    }

    const handleBookingExpanded = (id: number) => {
        navigate(`/viewBooking`, { state: { id, type: DataTypes.BOOKING } });
    }

    const handleApproveBooking = (id: number) => {
        if (id !== undefined) {
            dispatch(Actions.booking.approveBookingRequestAction.request({ booking_id: id }));
        }
    }

    const handleRejectBooking = (id: number) => {
        if (id !== undefined) {
            dispatch(Actions.booking.rejectBookingRequestAction.request({ booking_id: id }));
        }
    }

    return (
        <Paper elevation={3} sx={{borderRadius: 3, overflow: 'auto'}}>
            {getTableData(item)}
        </Paper>
    )
}

export default BuildBookingsTable;